/**
 * School Request Form Page - REFACTORED
 *
 * Modern implementation using:
 * - react-hook-form + Zod validation
 * - shadcn Form components
 * - SiiirSchoolSelector with auto-populate
 * - Reusable SchoolAddressFieldset component
 * - Design system utilities from main.css
 *
 * Replaces: Old version with manual state management
 *
 * Features:
 * - Status-based UI (PENDING/REJECTED/APPROVED)
 * - School conflict detection warnings
 * - SIIIR search with auto-populate (20+ fields)
 * - 8 required agreement checkboxes
 * - Conditional delivery address
 * - Direct API submission (no data transformation)
 *
 * Refactored: 2025-11-02
 * Version: 2.0
 */

import { useEffect, useState } from 'react';
import { useSchoolRequestWithForm } from '../hooks/useSchoolRequestWithForm';
import { useFormNavigation, useConfirmationDialog } from '@/shared/hooks';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SchoolAddressFieldset } from '@/shared/components/forms/SchoolAddressFieldset';
import { SiiirSchoolSelector } from '@/shared/components/forms/SiiirSchoolSelector';
import { NotaGradinite } from '@/domains/auth/components/NotaGradinite';
import { Spinner } from '@/components/ui/spinner';
import { FaRotate } from 'react-icons/fa6';
import { schoolRequestSchema } from '../validation/school-request.schemas';
import { DatePickerField } from '@/shared/components/forms/DatePickerField';
import { sirLaData, dataLaSir } from '@/shared/validation/saptamana-verde';
import { handleInvalidSubmit } from '@/shared/utils';
import { ScoalaOcupataDialog } from '../components/ScoalaOcupataDialog';
import { ScoalaOcupataPanou } from '../components/ScoalaOcupataPanou';
import { formularStrans } from '../utils/scoala-ocupata';
import { useEditionDocuments } from '@/shared/hooks/use-edition-documents';
import { RezumatCerere } from '../components/RezumatCerere';
import { BaraPasiInscriere } from '@/shared/components/BaraPasiInscriere';
import { INREGISTRARE_COD_ACTIV } from '@/domains/auth/utils/inregistrare-cod-flag';
import { trackSchoolRequestFailed } from '@/telemetry/posthog-events';
import { campuriDinProblemeZod } from '../utils/campuri-invalide';

/** Cele doua optiuni de livrare. `valoare` e booleanul din contractul cu backendul. */
const OPTIUNI_LIVRARE = [
  { valoare: true, text: 'La adresa școlii' },
  { valoare: false, text: 'La altă adresă' },
] as const;

export default function SchoolRequestFormPage() {
  const {
    form,
    handleFormSubmit,
    loading,
    isDirty,
    approvalStatus,
    rejectReason,
    schoolStatus,
    isError,
    refetch,
    loadingRequest,
  } = useSchoolRequestWithForm();

  // Navigation blocking when form has unsaved changes
  const navigationDialog = useConfirmationDialog({
    onConfirm: () => blocker.proceed?.(),
    onCancel: () => blocker.reset?.(),
  });

  const { blocker } = useFormNavigation({
    isDirty,
    onNavigationAttempt: navigationDialog.show,
  });

  // Submission confirmation
  const submitDialog = useConfirmationDialog({
    onConfirm: async () => {
      // getValues() intoarce valorile BRUTE ale formularului: `.default()` si
      // transformarile schemei nu s-au aplicat inca. Validarea a rulat deja inainte de
      // deschiderea dialogului, deci parse() trece si completeaza ce lipseste.
      try {
        await handleFormSubmit(schoolRequestSchema.parse(form.getValues()), { depuneTotusi });
      } catch (error) {
        // SE-07: `parse()` poate arunca daca datele s-au schimbat intre `trigger()` si
        // confirmare — `handleFormSubmit` isi are deja propriul try/catch, dar `parse()` insusi
        // e in afara lui. Fara asta, exceptia raman neprinsa (promisiune respinsa tacut), fara
        // niciun semnal pentru utilizator.
        console.error('School request parse error on confirm:', error);
        handleInvalidSubmit(undefined, undefined, () => form.formState.errors);
      }
    },
  });

  // Regulamentul editiei curente, ca link pe prima bifa (nu exista alt link catre el in formular).
  const documente = useEditionDocuments();

  // «Depune totuși cererea»: pentru o scoala „ocupata" formularul e strans pana apasa omul butonul.
  // Se reia de la zero la fiecare schimbare a codului de scoala — alta scoala ocupata, alta
  // decizie. Selectorul de scoala NU se strange niciodata: cine vrea alta scoala trebuie s-o poata alege.
  const [depuneTotusi, setDepuneTotusi] = useState(false);
  const codSiiir = form.watch('codSiiirUnitate');
  useEffect(() => {
    setDepuneTotusi(false);
  }, [codSiiir]);
  const strans = formularStrans(schoolStatus?.status, depuneTotusi);

  // Watch for state changes
  const sameAsSchool = form.watch('adresaLivrareAceeasiCuScoala');
  const codSe = form.watch('codSe');
  const [saptamanaVerdeStart, saptamanaVerdeNecunoscuta] = form.watch([
    'saptamanaVerdeStart',
    'saptamanaVerdeNecunoscuta',
  ]);

  // Status flags
  const isPending = approvalStatus === 'PENDING';
  const isRejected = approvalStatus === 'REJECTED';

  // Butonul „Salvare" e `type="button"`, ÎN AFARA `<form>` (CardFooter, sub CardContent) —
  // deliberat, nu se muta. Dar `<form onSubmit={(e) => e.preventDefault()}>` facea Enter
  // intr-un camp text un no-op TACUT: nici o eroare, nici dialogul, nimic (S1, p2-7). Enter
  // trebuie sa faca EXACT acelasi drum ca butonul — validare + dialog de confirmare, nu
  // trimitere directa — deci logica se extrage aici si se cheama din AMBELE locuri.
  // `strans`: cat timp formularul e strans sub «Depune totuși cererea», nici butonul „Salvare", nici
  // Enter nu trebuie sa trimita ceva ce omul nu vede. (Scoala deja aprobata nu mai BLOCHEAZA — vezi
  // `useSchoolRequestWithForm.handleSubmit`; a ramas doar poarta butonului.)
  const submitDisabled = !isDirty || loading || isError || strans;
  const attemptSubmit = async () => {
    if (submitDisabled) return;
    // Trigger validation before showing confirmation
    const isValid = await form.trigger();

    if (isValid) {
      submitDialog.show();
    } else {
      // Din aceeasi schema pe care o foloseste resolverul, nu din `formState.errors`: imediat dupa
      // `trigger()` acela e instantaneul randarii anterioare (vezi `handleInvalidSubmit`).
      const verificare = schoolRequestSchema.safeParse(form.getValues());
      trackSchoolRequestFailed('client', verificare.success ? [] : campuriDinProblemeZod(verificare.error.issues));
      // DEBUG: Show exact error details
      console.log('🔍 VALIDATION ERROR:');
      console.log('Errors object:', form.formState.errors);
      console.log('Form values:', form.getValues());

      // Show nested errors with full paths
      const printErrors = (obj: any, prefix = '') => {
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value && typeof value === 'object' && 'message' in value) {
            console.log(`  ❌ ${prefix}${key}: ${value.message}`);
          } else if (value && typeof value === 'object') {
            printErrors(value, `${prefix}${key}.`);
          }
        });
      };
      printErrors(form.formState.errors);
      console.log('---');

      // Acelasi drum ca pentru erorile venite de la backend (T7/D6): cautam
      // `[aria-invalid="true"]`, nu `[name=...]`. Cautarea dupa nume rata exact
      // campul care a produs raportul — comboboxul de scoala n-are atribut `name`.
      // Derularea e instanta si aseaza campul in treimea de sus, ca tastatura
      // virtuala de la 360px sa nu ajunga peste el.
      // SE-07: `formState.errors` cerut LAZY (getter), citit abia in interiorul rAF-ului din
      // `handleInvalidSubmit` — fallback pentru erori care nu marcheaza niciun input in DOM.
      handleInvalidSubmit(undefined, undefined, () => form.formState.errors);
    }
  };

  return (
    <div className="flex-1 flex flex-col md:overflow-hidden">
      <div className="flex-1 pr-4 ecotic-form-scrollbar md:overflow-y-auto">
        {/* Scrollerul e DOAR de la `md` in sus. Sub `md` formularul curge in
            pagina: un container derulabil pe telefon inseamna ca degetul misca
            altceva decat crede utilizatorul, si ascunde bara de actiuni sub el.
            Masurat la 360px: containerul chiar derula (scrollHeight > clientHeight). */}
        <div className="max-w-5xl mx-auto space-y-6 p-6 my-4">
          <Card className="w-full max-w-5xl">
        {/* Bara de pasi apare DOAR sub VITE_INREGISTRARE_COD (ruling S0, 2026-09-11) --
            fara flag, ecranul ramane identic cu PROD. */}
        {INREGISTRARE_COD_ACTIV && <BaraPasiInscriere pasCurent={2} />}
        <CardHeader>
          <CardTitle as="h1">Cerere Înscriere Școală</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6" aria-busy={loading}>
          {/* Randul asta lipsea din toate cele 16 masuratori S1 (p2-4): formularul se
              randa IMEDIAT, cu campurile doar `disabled` cat timp se incarca, fara
              niciun indicator vizibil sau anuntat asistiv - "role=status/aria-busy = 0"
              nu era o componenta stricata, era absenta completa a oricarei anuntari.
              `<Spinner>` (ui/spinner.tsx) are deja `role="status"` incorporat -
              consumat aici, nu modificat. */}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-2 text-dark/70">
              <Spinner className="size-5" />
              <span>Se încarcă...</span>
            </div>
          )}

          {/* ===================================================================
              STATUS ALERTS
              =================================================================== */}

          {/* Un 500/retea la incarcarea cererii proprii NU mai randa tacut formularul
              gol, ca si cum ar fi o cerere noua (silent 500, precedent
              use-editions.ts:27-35) - se afiseaza eroarea, cu reincercare directa pe
              interogare (`refetch`). Butonul „Reincarcare" din CardFooter foloseste
              acum acelasi `refetch` (P1-2) — pana atunci facea reload complet de
              pagina, reproducand flash-ul formularului la fiecare apasare. */}
          {isError && (
            <Alert className="border-error bg-error/10">
              <AlertDescription>
                <div className="text-center">
                  <p className="mb-2 font-semibold text-error">Nu am putut încărca datele cererii</p>
                  <p className="text-error">Verificați conexiunea și încercați din nou.</p>
                  <Button type="button" variant="outline" className="mt-3" onClick={() => refetch()}>
                    <FaRotate className="mr-2" />
                    <span>Încearcă din nou</span>
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* PENDING - Read-only message */}
          {isPending && (
            <Alert className="border-warning bg-warning/5">
              <AlertDescription>
                <div className="text-center">
                  <p className="mb-4 text-xl font-bold text-warning">
                    ⏳ Cererea ta de înscriere este în curs de aprobare
                  </p>
                  <p className="text-lg leading-relaxed text-warning">
                    Formularul tău a fost trimis cu succes și este în curs de verificare de către echipa ECOTIC.
                  </p>
                  <p className="mt-4 text-lg leading-relaxed text-warning">
                    Vei primi un email când cererea va fi aprobată.
                  </p>
                  {/* "toate functionalitatile" era fals: masurat pe DEV (P1-13), un
                      PROFESOR PENDING primeste 409 pe 4 din 12 intrari de sidebar
                      (lipsesc datele de scoala/editie), plus Colaboratori
                      neconstruibil fara schoolId -- acum ascunse din meniu (vezi
                      routes-provider.tsx, SHOW_CONDITION). Textul numeste ce chiar
                      functioneaza, nu mai promite "toate". */}
                  <p className="mt-4 text-sm text-warning">
                    Între timp, poți accesa exercițiile, activitățile practice, provocările,
                    mesajele și plasarea comenzii de colectare din meniul din stânga.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* REJECTED - Show reason + allow edit */}
          {isRejected && (
            <Alert className="border-error bg-error/10">
              <AlertDescription>
                <div className="text-center">
                  <p className="mb-4 text-xl font-bold text-error">
                    ❌ Cererea ta a fost respinsă
                  </p>
                  <p className="text-lg leading-relaxed text-error">
                    <strong>Motiv:</strong> {rejectReason || 'Nu a fost specificat un motiv'}
                  </p>
                  <p className="mt-4 text-sm text-error">
                    Poți modifica formularul de mai jos și îl poți retrimite.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Only show form once the initial load finished (state CUNOSCUTA -- P1-2),
              NOT pending, and data loaded correctly. `loadingRequest`, nu `loading`:
              acesta din urma ramane true si in timpul salvarii, cand formularul
              trebuie sa ramana vizibil (doar dezactivat). Fara garda, cat timp
              approvalStatus e inca `null` (stare necunoscuta, nu "nu e PENDING"),
              formularul se randa o clipa inaintea bannerului PENDING si dispare
              cand raspunsul soseste -- un flash la fiecare incarcare. */}
          {!loadingRequest && !isPending && !isError && (
            <>
              {/* ===================================================================
                  CONFLICT WARNINGS
                  =================================================================== */}

              {/* Doar NUMELE profesorului, niciodata emailul: raspunsul e vizibil oricui
                  tasteaza un cod SIIIR (decizie owner 2026-08-20). Dialogul e INFORMATIV pentru
                  APROBATA_EDITIA_CURENTA si IN_ASTEPTARE_EDITIA_CURENTA; EDITIE_ANTERIOARA n-are
                  dialog. Panoul cu butonul «Depune totuși cererea» sta sub selectorul de scoala. */}
              <ScoalaOcupataDialog
                status={schoolStatus?.status}
                numeProfesor={schoolStatus?.numeProfesor}
                onAlegeAltaScoala={() => form.setValue('codSiiirUnitate', '', { shouldDirty: true })}
                onDepuneTotusi={() => setDepuneTotusi(true)}
              />

              {/* ===================================================================
                  FORM CONTENT
                  =================================================================== */}

              <Form {...form}>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    // Nicio submisie NATIVA de Enter nu se putea intampla oricum: formularul
                    // n-are niciun `type="submit"` inauntru (butonul „Salvare" e in afara,
                    // deliberat), deci `onSubmit` de mai sus nu se declanseaza niciodata din
                    // tastatura -- verificat, nu presupus (Enter nu producea niciun eveniment
                    // `submit`). De-aia interceptarea se face pe `onKeyDown`, nu pe `onSubmit`.
                    // `Enter` intr-un `<textarea>` insereaza rand nou, nu trimite -- comportament
                    // nativ, nu exista niciun `<textarea>` in acest formular oricum (verificat).
                    if (e.key === 'Enter' && e.target instanceof HTMLElement && e.target.tagName !== 'TEXTAREA') {
                      e.preventDefault();
                      attemptSubmit();
                    }
                  }}
                  className="space-y-6"
                >
                  {/* SIIIR School Selector with Auto-populate */}
                  <FormField
                    control={form.control}
                    name="codSiiirUnitate"
                    render={({ field }) => (
                      <div className="space-y-2">
                        {/* `FormItem` invelie tot randul (eticheta + camp), nu doar campul --
                            altfel `FormLabel` citeste un `FormItemContext` gol (e sora, nu fiica),
                            si `htmlFor` nu mai corespunde cu id-ul real (S1, tabelul p2, randul
                            p2-6; acelasi tipar ca #177 si constatarea 41). */}
                        <FormItem className="form-field-row">
                          <FormLabel className="form-label-bold">Școala: *</FormLabel>
                          <div className="min-w-0 flex-1 space-y-0">
                            <FormControl>
                              <SiiirSchoolSelector
                                value={field.value}
                                onChange={field.onChange}
                                disabled={loading}
                                aria-required="true"
                                onSchoolSelect={(school) => {
                                  // Auto-populate all fields from SIIIR selection
                                  form.setValue('numeInstitutieInvatamant', school.numeInstitutieInvatamant || '');
                                  form.setValue('cui', school.cui || '');
                                  form.setValue('telefon', school.telefon || '');

                                  // Auto-populate school address
                                  if (school.judet || school.oras) {
                                    form.setValue('adresa', {
                                      judet: school.judet || '',
                                      oras: school.oras || '',
                                      comuna: school.comuna || '',
                                      sat: school.sat || '',
                                      strada: school.strada || '',
                                      numar: school.numar || '',
                                      codPostal: school.codPostal || '',
                                      bloc: '',
                                      scara: '',
                                      etaj: '',
                                      apartament: '',
                                    });
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage className="form-message-spacer" />
                            <NotaGradinite />
                          </div>
                        </FormItem>
                      </div>
                    )}
                  />

                  {/* Panoul «scoala ocupata»: mesaj + contact + butonul «Depune totuși cererea». Sta DIRECT
                      sub selector, care ramane mereu vizibil — cine vrea alta scoala trebuie s-o poata alege. */}
                  <ScoalaOcupataPanou
                    status={schoolStatus?.status}
                    numeProfesor={schoolStatus?.numeProfesor}
                    strans={strans}
                    onDepuneTotusi={() => setDepuneTotusi(true)}
                  />

                  {/* Tot ce urmeaza dupa selector se strange cu `hidden`, NU se demonteaza: starea
                      formularului si auto-completarea din SIIIR raman intacte, doar nu se vad si
                      nu se pot focaliza. */}
                  <div hidden={strans} className="space-y-6" data-formular-strans={strans}>
                    {/* School Name (Auto-populated, Read-only) */}
                    <FormField
                      control={form.control}
                      name="numeInstitutieInvatamant"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="form-field-row">
                            <FormLabel className="form-label-bold">Nume Instituție Învățământ:</FormLabel>
                            <div className="flex-1 space-y-0">
                              <FormControl>
                                <Input
                                  {...field}
                                  type="text"
                                  disabled
                                  className="form-input-styled bg-gray-50"
                                />
                              </FormControl>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Cod SE: doar CITIRE, niciodata editabil (decizie owner, 18.09). O scoala veche
                        il are; una noua il primeste la aprobare — deci cand e gol NU se randeaza un
                        camp gol si dezactivat, ci o linie care spune cand apare. Campul ramane in
                        starea formularului: cand vine o valoare (auto-completare, cerere existenta),
                        apare singur. */}
                    {codSe ? (
                      <FormField
                        control={form.control}
                        name="codSe"
                        render={({ field }) => (
                          <div className="space-y-2">
                            <FormItem className="form-field-row">
                              <FormLabel className="form-label-bold">Cod SE:</FormLabel>
                              <div className="flex-1 space-y-0">
                                <FormControl>
                                  <Input
                                    {...field}
                                    type="text"
                                    disabled
                                    className="form-input-styled bg-gray-50"
                                  />
                                </FormControl>
                                <FormMessage className="form-message-spacer" />
                              </div>
                            </FormItem>
                          </div>
                        )}
                      />
                    ) : (
                      <p className="m-0 text-sm text-gray-600" data-cod-se-la-aprobare>
                        Cod SE: se atribuie la aprobare.
                      </p>
                    )}

                    {/* CUI */}
                    <FormField
                      control={form.control}
                      name="cui"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="form-field-row">
                            <FormLabel className="form-label-bold">CUI: *</FormLabel>
                            <div className="flex-1 space-y-0">
                              <FormControl>
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="12345678"
                                  disabled={loading}
                                  aria-required="true"
                                  className="form-input-styled"
                                />
                              </FormControl>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Phone Number */}
                    <FormField
                      control={form.control}
                      name="telefon"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="form-field-row">
                            <FormLabel className="form-label-bold">Telefon: *</FormLabel>
                            <div className="flex-1 space-y-0">
                              <FormControl>
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="0712345678"
                                  disabled={loading}
                                  aria-required="true"
                                  className="form-input-styled"
                                />
                              </FormControl>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Number of Students */}
                    <FormField
                      control={form.control}
                      name="numarElevi"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="form-field-row">
                            <FormLabel className="form-label-bold">Număr Elevi: *</FormLabel>
                            <div className="flex-1 space-y-0">
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  min="1"
                                  disabled={loading}
                                  aria-required="true"
                                  className="form-input-styled"
                                  onChange={(e) => field.onChange(
                                    e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                                  )}
                                />
                              </FormControl>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Legal Representative */}
                    <FormField
                      control={form.control}
                      name="numeReprezentatLegal"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="form-field-row">
                            <FormLabel className="form-label-bold">Nume Reprezentant Legal: *</FormLabel>
                            <div className="flex-1 space-y-0">
                              <FormControl>
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="Nume complet"
                                  disabled={loading}
                                  aria-required="true"
                                  className="form-input-styled"
                                />
                              </FormControl>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* ===================================================================
                        SCHOOL ADDRESS SECTION
                        =================================================================== */}

                    <div className="w-full rounded-md bg-gray-50 p-3">
                      <div className="text-center text-lg font-bold text-gray-600">
                        Adresa Instituției de Învățământ
                      </div>
                    </div>

                    <SchoolAddressFieldset namePrefix="adresa" disabled={loading} ascundeDetaliiBloc />

                    {/* ===================================================================
                        DELIVERY ADDRESS SECTION
                        =================================================================== */}

                    {/* Livrarea: ALEGERE EXPLICITA, doua optiuni radio, NICIUNA preselectata (decizie
                        owner, 18.09). Era o bifa pre-bifata «Da»: cine nu se atingea de ea trimitea
                        «livrati la adresa scolii» fara sa fi ales. Lipsa alegerii e acum o eroare pe
                        camp (`MESAJ_LIVRARE_NEALEASA`). Radio NATIV: nu exista `RadioGroup` in `ui/`,
                        iar `min-h-11` tine tinta la 44px. `tabIndex={-1}` pe grup: `aria-invalid` ajunge
                        pe el prin `FormControl`, iar `focusFirstInvalidField` trebuie sa-l poata focaliza. */}
                    <FormField
                      control={form.control}
                      name="adresaLivrareAceeasiCuScoala"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="form-field-row">
                            <FormLabel className="form-label-bold">Livrarea se face: *</FormLabel>
                            <div className="flex-1 space-y-0">
                              <FormControl>
                                <div
                                  role="radiogroup"
                                  aria-label="Unde se face livrarea"
                                  aria-required="true"
                                  tabIndex={-1}
                                  className="flex flex-col gap-1"
                                >
                                  {OPTIUNI_LIVRARE.map(({ valoare, text }) => (
                                    <label
                                      key={text}
                                      className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-medium"
                                    >
                                      <input
                                        type="radio"
                                        name="adresaLivrareAceeasiCuScoala"
                                        className="size-5 shrink-0 accent-magenta"
                                        checked={field.value === valoare}
                                        onChange={() => {
                                          field.onChange(valoare);
                                          /* Masurat cu sonda (5b): dupa un Salvare esuat, mesajul ramane pe ecran langa
                                             radio-ul deja bifat pana la blur. Alegerea il stinge pe loc. */
                                          void form.trigger('adresaLivrareAceeasiCuScoala');
                                        }}
                                        onBlur={field.onBlur}
                                        disabled={loading}
                                      />
                                      {text}
                                    </label>
                                  ))}
                                </div>
                              </FormControl>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Delivery Address (Conditional). `=== false`, NU `!sameAsSchool`: pana la alegere
                        valoarea e `undefined`, iar sectiunea nu trebuie sa apara inainte ca omul sa
                        fi ales «la alta adresa». */}
                    {sameAsSchool === false && (
                      <>
                        <div className="w-full rounded-md bg-gray-50 p-3">
                          <div className="text-center text-lg font-bold text-gray-600">Adresa de Livrare</div>
                        </div>

                        <SchoolAddressFieldset namePrefix="adresaLivrare" disabled={loading} ascundeDetaliiBloc />
                      </>
                    )}

                    {/* 2.3a (raspuns ECOTIC 2026-09-10): intrebarea "recipiente" a iesit din UI --
                        Zod/DTO raman (schoolRequestSchema.detineRecipiente), cu default false din
                        defaultValues, nemodificat de nicaieri altundeva in formular. */}

                    {/* ===================================================================
                        SAPTAMANA VERDE (T12, p8) -- obligatorie SAU bifa (owner, 2026-09-16).
                        Era optionala; era `(opțional)`. Multe scoli inca n-o stiu la inscriere,
                        deci nu putem cere o data pe care n-o au -- de-aici bifa, nu un camp gol
                        acceptat tacut. Varianta cu coloana dedicata + reminder programat ramane
                        in issue #201, pentru cand ECOTIC decide fluxul; asta e doar UI, fara
                        nicio schimbare de schema pe backend.
                        =================================================================== */}

                    <div className="w-full rounded-md bg-gray-50 p-3">
                      <div className="text-center text-lg font-bold text-gray-600">Săptămâna Verde</div>
                    </div>

                    <FormField
                      control={form.control}
                      name="saptamanaVerdeNecunoscuta"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={loading}
                              id="saptamanaVerdeNecunoscuta"
                            />
                          </FormControl>
                          <FormLabel htmlFor="saptamanaVerdeNecunoscuta" className="text-sm font-normal cursor-pointer">
                            Nu cunosc încă intervalul, voi completa ulterior
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="saptamanaVerdeStart"
                      render={({ field, fieldState }) => (
                        <DatePickerField
                          value={sirLaData(field.value)}
                          onChange={(data) => field.onChange(dataLaSir(data))}
                          label="Început:"
                          error={fieldState.error?.message}
                          disabled={loading || !!saptamanaVerdeNecunoscuta}
                        />
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="saptamanaVerdeEnd"
                      render={({ field, fieldState }) => (
                        <DatePickerField
                          value={sirLaData(field.value)}
                          onChange={(data) => field.onChange(dataLaSir(data))}
                          label="Sfârșit:"
                          minDate={sirLaData(saptamanaVerdeStart)}
                          error={fieldState.error?.message}
                          disabled={loading || !!saptamanaVerdeNecunoscuta}
                        />
                      )}
                    />

                    {/* ===================================================================
                        TERMS AND CONDITIONS - 8 REQUIRED CHECKBOXES
                        =================================================================== */}

                    <div className="w-full rounded-md bg-gray-50 p-3">
                      <div className="text-center text-lg font-bold text-gray-600">Termeni și Condiții</div>
                    </div>

                    {/* Agreement 1: prinPrezentaConfirmamInscrierea */}
                    <FormField
                      control={form.control}
                      name="prinPrezentaConfirmamInscrierea"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="prinPrezentaConfirmamInscrierea"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel htmlFor="prinPrezentaConfirmamInscrierea" className="text-sm font-normal cursor-pointer">
                                Sunt cadru didactic în unitatea de învățământ pe care o reprezint și am studiat{' '}
                                {/* Linkul lipsea din formular. Se deschide in tab nou (formularul
                                    completat nu se pierde), iar `stopPropagation` il tine departe de
                                    bifa: atingerea linkului nu trebuie sa bifeze nimic. */}
                                <a
                                  href={documente.regulament}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline"
                                  data-link-regulament
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Regulamentul Campaniei
                                </a>
                                . *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 2: amFostInformati */}
                    <FormField
                      control={form.control}
                      name="amFostInformati"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="amFostInformati"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel htmlFor="amFostInformati" className="text-sm font-normal cursor-pointer">
                                Mă angajez să realizez un proiect educațional de educație pentru mediu în unitatea de
                                învățământ pe care o reprezint. *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 3: confirmFaptulCaReprezentatulLegal */}
                    <FormField
                      control={form.control}
                      name="confirmFaptulCaReprezentatulLegal"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="confirmFaptulCaReprezentatulLegal"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel
                                htmlFor="confirmFaptulCaReprezentatulLegal"
                                className="text-sm font-normal cursor-pointer"
                              >
                                Mă angajez să coordonez activitățile de colectare DEEE și DBA în unitatea de învățământ
                                pe care o reprezint. *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 4: prinPrezentaConfirmCaAmCititInformarea */}
                    <FormField
                      control={form.control}
                      name="prinPrezentaConfirmCaAmCititInformarea"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="prinPrezentaConfirmCaAmCititInformarea"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel
                                htmlFor="prinPrezentaConfirmCaAmCititInformarea"
                                className="text-sm font-normal cursor-pointer"
                              >
                                Îmi asum responsabilitatea datelor pe care le înscriu în platforma ECOTIC și voi
                                transmite aceste date către conducerea unității de învățământ pe care o reprezint. *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 5: confirmCaInCazulInCareProiectul */}
                    <FormField
                      control={form.control}
                      name="confirmCaInCazulInCareProiectul"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="confirmCaInCazulInCareProiectul"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel
                                htmlFor="confirmCaInCazulInCareProiectul"
                                className="text-sm font-normal cursor-pointer"
                              >
                                {/* Partea cu «Partenerii săi (Ministerul Educației, Inspectoratele Școlare
                                    Județene, Ministerul Mediului)» a fost scoasa (decizie owner, 18.09).
                                    Bifa RAMANE, cu textul taiat: e `requiredBoolean` in schema si in DTO-ul
                                    backendului, iar a o sterge din UI ar insemna sa-i trimitem `true` din
                                    cod — un consimtamant fabricat pe un camp de protectia datelor. Textul
                                    final il confirma clientul (C5). */}
                                Sunt de acord ca datele pe care le înscriu în această platformă să fie
                                transmise către ECOTIC. *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 6: consimtPentruPastrareaDatelorPersonale */}
                    <FormField
                      control={form.control}
                      name="consimtPentruPastrareaDatelorPersonale"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="consimtPentruPastrareaDatelorPersonale"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel
                                htmlFor="consimtPentruPastrareaDatelorPersonale"
                                className="text-sm font-normal cursor-pointer"
                              >
                                Îmi exprim acordul pentru transmiterea datelor personale și prelucrarea acestora în
                                conformitate cu Politica de confidențialitate și de prelucrare a datelor cu caracter
                                personal disponibilă pe www.ecotic.ro/politica-de-confidentialitate-si-de-prelucrare-datelor-cu-caracter-personal.
                                *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 7: confirmCaAmAcordulDePrincipiu */}
                    <FormField
                      control={form.control}
                      name="confirmCaAmAcordulDePrincipiu"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="confirmCaAmAcordulDePrincipiu"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel
                                htmlFor="confirmCaAmAcordulDePrincipiu"
                                className="text-sm font-normal cursor-pointer"
                              >
                                Mă angajez să colectez doar DEEE și DBA în unitatea de învățământ pe care o reprezint
                                conform categoriilor de produse menționate în Regulament. *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Agreement 8: amCititSiSuntDeAcord */}
                    <FormField
                      control={form.control}
                      name="amCititSiSuntDeAcord"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="amCititSiSuntDeAcord"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel htmlFor="amCititSiSuntDeAcord" className="text-sm font-normal cursor-pointer">
                                Îmi exprim acordul pentru publicarea în Platforma Școala Ecoterrienilor a pozelor
                                reprezentative cu activitățile desfășurate de mine/elevii mei în unitatea de
                                învățământ pe care o reprezint. *
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Optional Agreement: ecoticPoateFolosiImagini */}
                    <FormField
                      control={form.control}
                      name="ecoticPoateFolosiImagini"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="ecoticPoateFolosiImagini"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel htmlFor="ecoticPoateFolosiImagini" className="text-sm font-normal cursor-pointer">
                                Accept ca ECOTIC să folosească imaginile în materialele de promovare (opțional)
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Optional Agreement: acordGrupWhatsapp — textul acordului stă integral lângă bifă */}
                    <FormField
                      control={form.control}
                      name="acordGrupWhatsapp"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                                id="acordGrupWhatsapp"
                              />
                            </FormControl>
                            <div className="flex-1 space-y-1 leading-none">
                              <FormLabel htmlFor="acordGrupWhatsapp" className="text-sm font-normal cursor-pointer">
                                Sunt de acord să fiu adăugat(ă) în grupul de WhatsApp „Școala Ecoterrienilor”,
                                administrat de ECOTIC, cu numărul de telefon din contul meu. Numărul meu va fi
                                vizibil celorlalți membri ai grupului. Bifa este opțională, iar acordul îl pot
                                retrage oricând din această cerere. (opțional)
                              </FormLabel>
                              <FormMessage className="form-message-spacer" />
                            </div>
                          </FormItem>
                        </div>
                      )}
                    />
                  </div>
                </form>
              </Form>
            </>
          )}
        </CardContent>

        {/* ===================================================================
            ACTION BUTTONS
            =================================================================== */}
        {/* Butoanele au `w-full sm:w-auto` — dar randul nu se aseza NICIODATA pe
            coloana, deci doua butoane de latime 100% stateau unul langa altul
            intr-un rand de 294px: 246 + 16 + 246 = 508px. `justify-center`
            imparte depasirea in ambele parti, deci masurat la 360px pe build de
            productie: „Reincarcare" la left=-82 (in afara ecranului, la stanga,
            unde nu se poate derula) si butonul principal la right=426.
            Sub `sm` randul devine coloana; de la `sm` in sus, nimic nu se schimba. */}
        <CardFooter className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto"
            disabled={loading}
            onClick={() => refetch()}
          >
            <FaRotate className="mr-2" />
            <span>Reîncărcare</span>
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto"
            disabled={submitDisabled}
            onClick={attemptSubmit}
          >
            <span>{loading ? 'Se salvează...' : isRejected ? 'Retrimite' : 'Salvare'}</span>
          </Button>
        </CardFooter>
          </Card>
        </div>
      </div>

      {/* Navigation confirmation - shown when trying to leave page with unsaved changes */}
      <ConfirmationDialog
        isOpen={navigationDialog.isOpen}
        onClose={navigationDialog.handleCancel}
        onConfirm={navigationDialog.handleConfirm}
        title="Modificări nesalvate"
        description="Aveți modificări nesalvate. Doriți să părăsiți pagina?"
        confirmText="Părăsește pagina"
        cancelText="Rămâi pe pagină"
        variant="destructive"
      />

      {/* Submission confirmation - shown when clicking save button */}
      <ConfirmationDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.handleCancel}
        onConfirm={submitDialog.handleConfirm}
        title="Confirmare salvare"
        description="Verificați datele înainte de a trimite cererea."
        confirmText="Salvează"
        cancelText="Anulare"
        loading={loading}
      >
        <RezumatCerere date={form.getValues()} />
      </ConfirmationDialog>
    </div>
  );
}
